import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flame, MapPin, Calendar, TrendingUp, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrendingEventsSection({ limit = 4, city = null, sector = null, title = "Trending Events" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTrending = React.useCallback(async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/public/trending?limit=${limit}`;
      if (city) url += `&city=${city}`;
      if (sector) url += `&sector=${sector}`;
      
      const response = await axios.get(url);
      setEvents(response.data.trending || []);
    } catch (err) {
      console.error('Failed to fetch trending:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, city, sector]);

  useEffect(() => {
    fetchTrending();
  }, [fetchTrending]);

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <section className="py-8">
      <div className="flex items-center gap-2 mb-6">
        <Flame className="w-6 h-6 text-orange-500" />
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <Badge className="bg-orange-100 text-orange-700 ml-2">
          <TrendingUp className="w-3 h-3 mr-1" />
          Hot Right Now
        </Badge>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {events.map((event, idx) => (
          <Link 
            key={event.id}
            to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
            className="group"
          >
            <Card className="h-full hover:shadow-lg transition-all duration-200 border-2 border-transparent hover:border-orange-200 overflow-hidden">
              <CardContent className="p-4">
                {/* Header with rank badge inline */}
                <div className="flex items-start gap-2 mb-2">
                  {idx < 3 && (
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                      idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 
                      idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 
                      'bg-gradient-to-br from-orange-400 to-orange-600'
                    }`}>
                      {idx + 1}
                    </div>
                  )}
                  <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2 flex-1">
                    {event.name}
                  </h3>
                  {event.trending && (
                    <Badge className="bg-orange-500 text-white text-xs flex-shrink-0">
                      <Flame className="w-3 h-3 mr-0.5" />
                      Hot
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-1.5 text-sm text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-orange-500" />
                    <span>{event.venue}, {event.city}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-orange-500" />
                    <span>{event.dates?.display || 'TBC'}</span>
                  </div>
                </div>
                
                {/* Sector Tags */}
                <div className="flex flex-wrap gap-1 mt-3">
                  {event.sectorTags?.slice(0, 2).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                {/* Popularity Indicator */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <TrendingUp className={`w-3 h-3 ${event.popularityScore > 50 ? 'text-green-500' : 'text-gray-400'}`} />
                    <span>Popularity</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          event.popularityScore >= 60 ? 'bg-green-500' :
                          event.popularityScore >= 30 ? 'bg-yellow-500' :
                          'bg-gray-300'
                        }`}
                        style={{ width: `${Math.min(event.popularityScore, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
